import React from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, MessageCircle, Share2, Globe, MoreHorizontal, Music } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import type { PreviewProps } from './types';

export const FacebookPreview: React.FC<PreviewProps> = ({ content, brand, mediaUrl, mediaUrls, format = 'regular', hideEngagementCounts }) => {
  const { t } = useTranslation();
  const renderLinkedText = (text: string) => {
    const elements: React.ReactNode[] = [];
    const lines = (text || '').split('\n');
    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+|#[\p{L}\d_]+)/giu;
    lines.forEach((line, lineIdx) => {
      if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(line)) !== null) {
        const start = match.index;
        if (start > lastIndex) {
          elements.push(line.slice(lastIndex, start));
        }
        const token = match[0];
        if (token.startsWith('#')) {
          elements.push(
            <span key={`hash-${lineIdx}-${start}`} className="text-[#0063D1]">{token}</span>
          );
        } else {
          const href = token.startsWith('http') ? token : `http://${token}`;
          elements.push(
            <a key={`link-${lineIdx}-${start}`} href={href} target="_blank" rel="noreferrer" className="text-[#0063D1] hover:underline">
              {token}
            </a>
          );
        }
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < line.length) {
        elements.push(line.slice(lastIndex));
      }
    });
    return elements;
  };
  // Regular Facebook post
  if (format === 'regular') {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-100 overflow-hidden">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">
                    {brand?.name || 'Company Name'}
                  </h3>
                </div>
                <p className="text-xs text-gray-500 flex items-center">
                  3h • <Globe className="w-3 h-3 ml-1" />
                </p>
              </div>
            </div>
            <div>
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreHorizontal className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <p className="text-gray-900 break-words" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
            {renderLinkedText(content)}
          </p>
        </div>

        {/* Media: single image or multi */}
        {(mediaUrls && mediaUrls.length > 0) ? (
          <div className="bg-gray-100">
            {(() => {
              const urls = mediaUrls.slice(0, 5); // show up to 5 thumbs
              const remaining = mediaUrls.length - urls.length;
              // layout: first row 2, second row 3
              const firstRow = urls.slice(0, 2);
              const secondRow = urls.slice(2);
              return (
                <div className="w-full">
                  <div className="grid grid-cols-2 gap-px">
                    {firstRow.map((u, i) => (
                      <div key={`r1-${i}`} className="relative w-full h-full" style={{aspectRatio: '1 / 1'}}>
                        <MediaDisplay mediaUrl={u} altText="Facebook post" type="image" className="w-full h-full" objectFit="cover" />
                      </div>
                    ))}
                  </div>
                  {secondRow.length > 0 && (
                    <div className="grid grid-cols-3 gap-px mt-px">
                      {secondRow.map((u, i) => {
                        const isLast = i === secondRow.length - 1 && remaining > 0;
                        return (
                          <div key={`r2-${i}`} className="relative w-full h-full" style={{aspectRatio: '1 / 1'}}>
                            <MediaDisplay mediaUrl={u} altText="Facebook post" type="image" className="w-full h-full" objectFit="cover" />
                            {isLast && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <span className="text-white font-semibold text-xl">+{remaining + 1}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : mediaUrl ? (
          <div className="w-full bg-gray-100 min-h-[200px] max-h-[400px]">
            <MediaDisplay
              mediaUrl={mediaUrl}
              altText="Facebook post"
              type="image"
              className="w-full h-full object-contain"
              objectFit="contain"
            />
          </div>
        ) : null}

        {/* Engagement Stats */}
        {!hideEngagementCounts && (
          <div className="px-4 py-2 border-t border-b">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <div className="flex items-center">
                  <div className="bg-blue-500 rounded-full p-1">
                    <ThumbsUp className="w-2 h-2 text-white" />
                  </div>
                  <span className="ml-1">425</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span>64 {t('previews.comments', 'comments')}</span>
                <span>32 {t('previews.actions.share', 'Share')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="px-4 py-1 flex">
          <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-100 rounded">
            <ThumbsUp className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-500">{t('previews.actions.like', 'Like')}</span>
          </button>

          <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-100 rounded">
            <MessageCircle className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-500">{t('previews.actions.comment', 'Comment')}</span>
          </button>

          <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-100 rounded">
            <Share2 className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-500">{t('previews.actions.share', 'Share')}</span>
          </button>
        </div>
      </div>
    );
  }
  
  // Facebook Reels preview
  return (
    <div className="bg-black text-white rounded-xl shadow-md border border-gray-800 overflow-hidden max-w-[320px] mx-auto">
      <div className="relative w-full aspect-[9/16] bg-gray-900 flex flex-col">
        {/* Main content */}
        <div className="flex-1 relative">
          <MediaDisplay
            mediaUrl={mediaUrl}
            altText="Facebook Reel"
            type="video"
            fullSize={false}
            className="h-full w-full object-cover"
            controls={false}
            autoPlayOnMount
            loop
            muted
            aspectRatio="9/16"
          />
          
          {/* Right side actions */}
          <div className="absolute right-3 bottom-24 flex flex-col items-center space-y-5">
            <button className="flex flex-col items-center">
              <ThumbsUp className="w-7 h-7" />
              <span className="text-xs mt-1">87k</span>
            </button>
            <button className="flex flex-col items-center">
              <MessageCircle className="w-7 h-7" />
              <span className="text-xs mt-1">543</span>
            </button>
            <button className="flex flex-col items-center">
              <Share2 className="w-7 h-7" />
              <span className="text-xs mt-1">126</span>
            </button>
            <button className="flex flex-col items-center">
              <MoreHorizontal className="w-7 h-7" />
            </button>
          </div>

          {/* Top profile */}
          <div className="absolute top-0 left-0 right-0 p-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gray-100 overflow-hidden">
                {brand?.logo ? (
                  <img src={brand.logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200" />
                )}
              </div>
              <span className="font-semibold text-sm flex-1">{brand?.name || 'Company Name'}</span>
              <button className="text-blue-500 text-sm font-medium">
                {t('previews.follow', 'Follow')}
              </button>
            </div>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
            <p className="mb-3" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{renderLinkedText(content)}</p>
            <div className="flex items-center">
              <Music className="w-3 h-3 mr-2" />
              <div className="flex-1 text-xs truncate">
                {t('previews.originalAudio', 'Original audio')} • {brand?.name || 'Company Name'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};